import { FC, useState } from "react";
import { ThemeProvider, Theme, StyledEngineProvider, Tooltip } from "@mui/material";
import { lightTheme } from "theme";
import makeStyles from "@mui/styles/makeStyles";
import AlertIconList from "components/portfolio/components/alert/AlertIconList";
import { AlertIconProps } from "components/portfolio/components/alert/alert-icon-type";
import { useAlertsByAccountKeyAndType } from "components/portfolio/hooks";
import { useAxios } from "myAxios";
import { dismissCallback } from "components/portfolio/components/alert/index";

declare module "@mui/styles/defaultTheme" {
  // eslint-disable-next-line
  interface DefaultTheme extends Theme {}
}

const useStyles = makeStyles(() => ({
  tooltip: { minWidth: 360, padding: 16, maxHeight: "unset" },
  titleContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingRight: 2,
  },
  titleText: {
    fontSize: 13,
    fontWeight: 500,
    textTransform: "uppercase",
  },
  action: {
    fontSize: 12,
    color: "#00A0D1",
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

export const AlertIcon: FC<AlertIconProps> = ({
  type,
  accountKey,
  gridEvent,
  mutateKeys,
  accountLevelMutateKeys,
}) => {
  const [active, setActive] = useState(false);
  const classes = useStyles();
  const axios = useAxios();

  if (!accountKey) return null;

  //when hover open, then start to fetch data
  const { data: alerts, error } = useAlertsByAccountKeyAndType(
    active ? accountKey : undefined,
    type
  );

  const loading = alerts === undefined;

  const onOpen = () => setActive(true);
  const onClose = () => setActive(false);

  const dismissAll = () => {
    const payload = {
      accountKeys: [accountKey],
      alertType: type,
    };
    axios.post(`/api/dataservice/v2/alerts/dismiss-alert`, payload).then(() => {
      dismissCallback(
        { ...payload, dismissAllCount: 1 },
        gridEvent,
        mutateKeys,
        accountLevelMutateKeys
      );
    });
  };

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={lightTheme}>
        <Tooltip
          placement={"right"}
          classes={{ tooltip: classes.tooltip }}
          open={active}
          onClose={onClose}
          title={
            <div>
              <div className={classes.titleContainer}>
                <span className={classes.titleText}>
                  <img
                    src={`/portfolio/${type.toLocaleLowerCase()}-alert-title.svg`}
                    style={{ marginRight: 8 }}
                    width={18}
                    height={18}
                  />
                  {type} ({error ? 0 : loading ? "..." : alerts?.length})
                </span>
                <span
                  className={classes.action}
                  onClick={dismissAll}
                >
                  Dismiss All
                </span>
              </div>
              {error ? (
                <p>Fail to load alerts</p>
              ) : (
                <AlertIconList
                  type={type}
                  alerts={alerts}
                  gridEvent={gridEvent}
                  mutateKeys={mutateKeys}
                  accountKey={accountKey}
                  accountLevelMutateKeys={accountLevelMutateKeys}
                />
              )}
            </div>
          }
        >
          <img
            src={`/portfolio/${type.toLocaleLowerCase()}-alert${active ? "-active" : ""}.svg`}
            onMouseEnter={onOpen}
          />
        </Tooltip>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default AlertIcon;
